import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, IconButton, InputBase, Tooltip } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useToast } from "../../services/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { Search } from "@mui/icons-material";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import useFetchPaginatedFileProcessing from "../../hooks/useFetchPaginatedFileProcessing";
import useDebounce from "../../hooks/useDebounce";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import ButtonComponent from "../Button.Component";
import {
  deleteFileProcessing,
  reactivateFileProcessing,
} from "../../services/fileProcessingService";
import { getFormattedDateTime } from "../../services/utils";
import {
  IconDeleteBlueSvg,
  IconEditBase,
  IconEyeBase,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import Loader from "../Molecules/Loader/Loader";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import { paginatedResponseFormat } from "../../services/constants";
import ConfirmDailog from "../Dialogs/ConfirmDailog";
import SearchFilterBar from "../Molecules/FilterSearch/SearchFilterBar";

// Centralized Filter Configuration for File Processing Attributes List
const FILE_PROCESSING_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    resource_type: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["resource_type", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const FileProcessingAttributes: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { rowsPerPage } = useRowsPerPage();
  // states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<any>(paginatedResponseFormat);
  const [fileProcessingData, setFileProcessingData] = useState<any[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [fileProcessingToDelete, setFileProcessingToDelete] =
    useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [page, setPage] = useState<number>(1);
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  // SearchFilterBar state - initialize from URL parameters
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...FILE_PROCESSING_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    FILE_PROCESSING_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        initialState[field] = urlValue;
      }
    });

    return initialState;
  });

  // Extract filters from searchFilterData
  const effectiveStatusFilter = searchFilterData?.status_filter || "";
  const effectiveResourceType = searchFilterData?.resource_type || "";

  // hooks
  const [fetchedFileProcessings] = useFetchPaginatedFileProcessing({
    setIsLoading: setIsDownloadLoading,
    page,
    pSize,
    searchTerm: effectiveResourceType,
    statusFilter: effectiveStatusFilter,
  });

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    FILE_PROCESSING_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...FILE_PROCESSING_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    FILE_PROCESSING_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        urlFilterState[field] = urlValue;
      }
    });

    // Only update state if URL differs from current state
    const hasChanges = FILE_PROCESSING_FILTER_CONFIG.URL_FIELDS.some(
      (field) => urlFilterState[field] !== searchFilterData[field]
    );

    if (hasChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams]);

  // Reset page to 1 when search filter changes
  useEffect(() => {
    if (
      searchFilterData?.resource_type !== undefined ||
      searchFilterData?.status_filter !== undefined
    ) {
      setPage(1);
    }
  }, [searchFilterData]);

  const handelClickEvent = () => {
    navigate("/file-processing-attributes/add");
  };

  // Handle delete file processing
  const handleDeleteFileProcessing = (fileProcessing: any) => {
    setFileProcessingToDelete(fileProcessing);
    setDeleteConfirmOpen(true);
  };

  // Handle reactivate file processing
  const handleReactivate = async (id: number, resourceType: string) => {
    setIsLoading(true);
    try {
      await reactivateFileProcessing(id);
      showToast(
        `File Processing Attribute "${resourceType}" reactivated successfully`,
        "success"
      );

      // Optimistic UI update - immediately update the is_active flag in local state
      setFileData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === id ? { ...item, is_active: true } : item
        ),
      }));
    } catch (error) {
      showToast("Failed to reactivate file processing attribute", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!fileProcessingToDelete) return;
    try {
      setDeleteLoading(true);
      const result = await deleteFileProcessing(fileProcessingToDelete.id);
      if (result && result.status === 200) {
        showToast(
          `"${fileProcessingToDelete.resource_type}" deleted successfully`,
          "success"
        );
        setDeleteConfirmOpen(false);
        setFileProcessingToDelete(null);
        // Update the file processing list to remove the deleted item
        setFileData((prev: any) => {
          const updatedItems = (prev?.items || []).filter(
            (fp: any) => fp.id !== fileProcessingToDelete.id
          );
          return {
            ...prev,
            items: updatedItems,
            total: Math.max(0, (prev?.total || 0) - 1),
          };
        });
        setFileProcessingData((prev: any[]) =>
          prev.filter((fp: any) => fp.id !== fileProcessingToDelete.id)
        );
      }
    } catch (error: any) {
      showToast(error?.message || "Failed to delete file processing", "error");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
      renderCell: (params: any) => <></>,
    },
    {
      field: "id",
      headerName: "ID",
      minWidth: 80,
      flex: 1,
      renderCell: (params: any) => {
        return <span>{params.id}</span>;
      },
    },
    {
      field: "resource_type",
      headerName: "Resource Type",
      minWidth: 140,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "modified_by_user",
      headerName: "Modified By",
      minWidth: 140,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value?.username} />;
      },
    },
    {
      field: "updated_on",
      headerName: "Last Modified",
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => {
        return getFormattedDateTime(params?.value);
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        return params.value ? (
          <Tooltip title="Active" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Inactive" placement="top" arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      width: 170,
      renderCell: (params: any) => {
        const handleDelete = () => {
          handleDeleteFileProcessing(params.row);
        };
        const isActive = params.row.is_active === true;

        return (
          <>
            {/* For active records, show all action buttons */}
            {isActive && (
              <>
                <Tooltip title="View FileProcessing" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => {
                      navigate(
                        `/file-processing-attributes/view/${params.row.id}`
                      );
                    }}
                    aria-label="View file processing attributes"
                    data-testid={`view-file-processing-${params.row.id}-btn`}
                  >
                    <IconEyeBase />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit FileProcessing" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => {
                      navigate(
                        `/file-processing-attributes/edit/${params.row.id}`
                      );
                    }}
                    aria-label="Edit file processing attributes"
                    data-testid={`edit-file-processing-${params.row.id}-btn`}
                  >
                    <IconEditBase />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete FileProcessing" placement="top" arrow>
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => handleDelete()}
                  >
                    <IconDeleteBlueSvg />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* For inactive records, show ONLY Reactivate button */}
            {!isActive && (
              <Tooltip title="Reactivate FileProcessing" placement="top" arrow>
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    handleReactivate(params.row.id, params.row.resource_type)
                  }
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
            {/* <DropdownMenu
              dropdownMenuItems={[
                <IconButton
                  key="export"
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    downloadFileProcessingAttributesExportFile(
                      params.row.id,
                      setIsDownloadLoading
                    )
                  }
                >
                  <IconExportIconBlue />
                  Export
                </IconButton>,
              ]}
            /> */}
          </>
        );
      },
    },
  ];

  // for update file processing attributes list data
  useEffect(() => {
    if (fetchedFileProcessings) {
      setFileData(fetchedFileProcessings);
    }
  }, [fetchedFileProcessings]);

  return (
    <>
      <Loader isLoading={isDownloadLoading} />
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle="Delete File Processing"
        dailogDescription={`Are you sure you want to delete file processing "${fileProcessingToDelete?.resource_type}"?`}
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              setSearchFilterData={setSearchFilterData}
              searchFilterData={searchFilterData}
              requiredFields={["resource_type", "status_filter"]}
              primaryFields={["resource_type", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
              }}
            />
          </Grid>
          <Grid
            item
            xs="auto"
            sx={{ display: "flex", alignItems: "flex-start" }}
          >
            <ButtonComponent
              className="btn-orange"
              handelClickEvent={handelClickEvent}
            >
              <AddSharpIcon sx={{ marginRight: "4px" }} /> File Processing
              Attributes
            </ButtonComponent>
          </Grid>
        </Grid>
      </Box>
      <FlexBetween>
        <DataTable
          dataColumns={columns || []}
          dataRows={fileData?.items || []}
          loading={isLoading}
          dataListTitle={"File Processing Attributes List"}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total || 0}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={378}
        />
      </FlexBetween>
    </>
  );
};

export default FileProcessingAttributes;
